import { LeafletMouseEvent, Marker as LeafletMarker } from "leaflet"
import { Card, Icon, Surface, Text } from "@/components/ui"
import { Pin } from "@/icons"
import { useEffect, useMemo, useRef } from "react"
import { Circle, MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet"

import { mapPinIcon, paletteColor } from "@/components/maps/map-pin"
import { MAP_TILE, MUTED_TILES_CLASS } from "@/components/maps/tiles"

interface InteractiveMapProps {
  lat: number
  lng: number
  /**
   * Called when the user moves the pin (click or dragend). MUST be cheap —
   * only update local state. Do NOT reverse-geocode here; confirm once via a
   * dedicated CTA in the parent (iFood/Uber pattern).
   */
  onPositionChange: (lat: number, lng: number) => void
  disabled?: boolean
  /** When false, click and drag leave the pin where it is. The map itself still pans. */
  movable?: boolean
  /** Hint shown on the map card. */
  hint?: string
  /** Circle the pin must stay inside: the typed address' geocode and the drift the quote tolerates. */
  reach?: { lat: number; lng: number; radiusMeters: number; exceeded: boolean }
}

const DEFAULT_CENTER: [number, number] = [-23.5505, -46.6333]
const DEFAULT_ZOOM = 18

function Recenter({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap()
  useEffect(() => {
    if (lat && lng) {
      map.setView([lat, lng], map.getZoom())
    }
  }, [lat, lng, map])
  return null
}

function ClickCapture({
  onPositionChange,
  disabled,
}: {
  onPositionChange: (lat: number, lng: number) => void
  disabled: boolean
}) {
  useMapEvents({
    click(event: LeafletMouseEvent) {
      if (disabled) return
      onPositionChange(event.latlng.lat, event.latlng.lng)
    },
  })
  return null
}

function InteractionToggle({ disabled }: { disabled: boolean }) {
  const map = useMap()
  useEffect(() => {
    const handlers = [
      map.dragging,
      map.scrollWheelZoom,
      map.doubleClickZoom,
      map.touchZoom,
      map.boxZoom,
      map.keyboard,
    ]
    for (const handler of handlers) {
      if (disabled) handler.disable()
      else handler.enable()
    }
  }, [disabled, map])
  return null
}

export default function InteractiveMap({
  lat,
  lng,
  onPositionChange,
  disabled = false,
  movable = true,
  reach,
  hint = "Arraste o pin até o local exato. A confirmação é no botão abaixo — sem custo a cada movimento.",
}: InteractiveMapProps) {
  const hasPosition = Boolean(lat && lng)
  const markerRef = useRef<LeafletMarker>(null)

  const markerHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current
        if (!marker) return
        const position = marker.getLatLng()
        onPositionChange(position.lat, position.lng)
      },
    }),
    [onPositionChange],
  )

  return (
    <div className="w-full h-full relative">
      <MapContainer
        center={hasPosition ? [lat, lng] : DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        className="!h-full !w-full isolate rounded-card"
        scrollWheelZoom
      >
        <TileLayer {...MAP_TILE} className={MUTED_TILES_CLASS} />
        <Recenter lat={lat} lng={lng} />
        {movable && <ClickCapture onPositionChange={onPositionChange} disabled={disabled} />}
        <InteractionToggle disabled={disabled} />
        {reach && (
          <Circle
            center={[reach.lat, reach.lng]}
            radius={reach.radiusMeters}
            pathOptions={{
              color: reach.exceeded ? paletteColor("danger") : paletteColor("success"),
              weight: 2,
              fillOpacity: 0.06,
              dashArray: "6 6",
            }}
          />
        )}
        {hasPosition && (
          <Marker
            ref={markerRef}
            position={[lat, lng]}
            icon={mapPinIcon}
            draggable={movable && !disabled}
            eventHandlers={movable ? markerHandlers : undefined}
          />
        )}
      </MapContainer>

      <Card variant="glass" padding="compact" className="absolute left-2 right-2 top-2 z-sticky-raised">
        <Text size="sm" weight="medium" leading="snug" tone="muted">
          <Icon as={Pin} size="sm" tone="primary" className="-mt-0.5 mr-1 inline" />
          {hint}
        </Text>
      </Card>

      {disabled && (
        <div className="pointer-events-none absolute inset-0 z-sticky-raised flex cursor-not-allowed items-center justify-center">
          <Surface surface="card" aria-hidden className="absolute inset-0 rounded-card opacity-40" />
          <Card variant="glass" padding="compact">
            <Text size="sm" weight="medium" tone="muted">
              Aguarde um instante…
            </Text>
          </Card>
        </div>
      )}
    </div>
  )
}
